import _, { get, toNumber } from 'lodash';
import AreaChart from '../charts/AreaChart';
import AreaLineChart from '../charts/AreaLineChart';
import BarChart from '../charts/BarChart';
import BarLine from '../charts/ColumnLineCombo/BarLine';
import FunnelChart from '../charts/Funnel';
import LineChart from '../charts/Line/LineChart';
import { PivotTable } from '../charts/pivot';
import StackBarChart from '../charts/StackBar';
import StackChart from '../charts/StackChart';
import StackLineChart from '../charts/StackLineCombo';
import { PipelineChangesChart } from '../charts/pipeline-changes/pipeline-changes.component';
import BaseChartSpec from '../BaseChartSpec';
import { IViz } from '../interfaces';
import { IVizQueryExecuteQueryResults } from '../../common/graphql/viz-query.interfaces';
import { ChartSpecs } from '../ChartSpecs';
import { WaterfallChart } from '../charts/waterfall/waterfall.component';
import { ChartContext } from './chart.hook';
import { use, useMemo } from 'react';
import { ChartWindowContext, VizContext } from './viz-chart.hook';

export interface IChartProps {
  key: string;
  spec?: BaseChartSpec;
  viz?: IViz;
  vizId?: string;
  queryResults: string | any;
  secondaryQueryResults?: IVizQueryExecuteQueryResults;
  height?: string | number;
  width?: string | number;
}

// @NOTE deprecated props are from LegacyVizChart usage and should be removed
export const LegacyChart = (props: IChartProps) => {
  const { width, height } = props;
  if (!_.isNumber(height) || height <= 0 || !_.isNumber(width) || width <= 0) {
    console.warn(
      `Invalid dimensions for chart. height: ${height} width: ${width}`,
    );
    return null;
  }

  return (
    <ChartContext.Provider
      value={{
        width: toNumber(width ?? 0),
        height: toNumber(height ?? 0),
        queryResults: props?.queryResults,
      }}
    >
      <ChartByType {...props} />
    </ChartContext.Provider>
  );
};

const ChartByType = props => {
  switch (props?.spec.id) {
    case ChartSpecs.line.id: {
      return <LineChart {...props} />;
      break;
    }
    case ChartSpecs.area_line.id: {
      return (
        <AreaLineChart
          {...props}
          primaryPlotProps={{
            hidePoints: true,
            disableLineSmoothing: true,
            className: 'mixed-area-line',
            showLastPoint: false,
          }}
          secondaryPlotProps={{ className: 'mixed-area' }}
        />
      );
    }
    case ChartSpecs.area.id: {
      return (
        <AreaChart
          {...props}
          secondaryUsesPrimaryScale
          alignYAxesAtZero='true'
        />
      );
    }
    case ChartSpecs.column_line.id: {
      return <BarLine {...props} />;
    }
    case ChartSpecs.stack.id: {
      return <StackChart {...props} />;
    }
    case ChartSpecs.stack_bar.id: {
      return <StackBarChart {...props} />;
    }
    case ChartSpecs.pivot.id: {
      return <PivotTable {...props} />;
    }
    case ChartSpecs.funnel.id: {
      return <FunnelChart {...props} />;
    }
    case ChartSpecs.stack_line.id: {
      return <StackLineChart {...props} />;
    }
    case ChartSpecs.pipeline_changes.id: {
      return <PipelineChangesChart {...props} />;
    }
    case ChartSpecs.waterfall.id: {
      return <WaterfallChart {...props} />;
    }
    default:
      return <BarChart {...props} />;
  }
};

// @NOTE deprecated props are from LegacyVizChart usage and should be removed
export const Chart = ({
  queryResults,
  viz: _deprecatedViz,
  width: _deprecatedWidth,
  height: _deprecatedHeight,
}: IChartProps) => {
  const { width: _width, height: _height } = use(ChartWindowContext);
  const width = _deprecatedWidth ?? _width;
  const height = _deprecatedHeight ?? _height;

  const _viz = use(VizContext);
  const viz = _deprecatedViz ?? _viz;

  const {
    width: widthProp,
    height: heightProp,
    vizId,
    spec,
    queryResults: queryResultsProp,
    viz: vizProp,
  } = useMemo(
    () => ({
      width,
      height,
      vizId: viz?.id,
      spec: get(ChartSpecs, viz?.chartType),
      queryResults,
      viz,
    }),
    [width, height, viz, queryResults],
  );

  if (!_.isNumber(height) || height <= 0 || !_.isNumber(width) || width <= 0) {
    console.warn(
      `Invalid dimensions for chart. height: ${height} width: ${width}`,
    );
    return null;
  }

  return (
    <ChartContext.Provider
      value={{
        width: toNumber(width ?? 0),
        height: toNumber(height ?? 0),
        queryResults,
      }}
    >
      <ChartByType
        {...{
          width: widthProp,
          height: heightProp,
          vizId,
          spec,
          queryResults: queryResultsProp,
          viz: vizProp,
        }}
      />
    </ChartContext.Provider>
  );
};
